import React, { useState } from "react";
import { 
  CogIcon, 
  ExclamationTriangleIcon, 
  ClipboardDocumentListIcon,
  CalendarDaysIcon,
  UserGroupIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ShieldCheckIcon
} from "@heroicons/react/24/outline";
import type { TechnicalInsightsProps } from "./technicalInsights.types";

export default function TechnicalInsights({ technicalSolution }: TechnicalInsightsProps) {
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    designPatterns: true, // Expandir por padrão
    technicalRisks: true
  });

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  if (!technicalSolution) return null;

  const {
    designPatterns,
    nonFunctionalRequirements,
    technicalRisks,
    implementationStrategy,
    developmentPhases,
    teamRequirements
  } = technicalSolution;

  const sections = [
    {
      key: 'designPatterns',
      title: 'Padrões de Design',
      icon: CogIcon,
      color: 'text-blue-600 bg-blue-50 border-blue-200',
      data: designPatterns,
      type: 'array' as const
    },
    {
      key: 'nonFunctionalRequirements',
      title: 'Requisitos Não Funcionais',
      icon: ShieldCheckIcon,
      color: 'text-green-600 bg-green-50 border-green-200',
      data: nonFunctionalRequirements,
      type: 'object' as const
    },
    {
      key: 'technicalRisks',
      title: 'Riscos Técnicos',
      icon: ExclamationTriangleIcon,
      color: 'text-red-600 bg-red-50 border-red-200',
      data: technicalRisks,
      type: 'array' as const
    },
    {
      key: 'implementationStrategy',
      title: 'Estratégia de Implementação',
      icon: ClipboardDocumentListIcon,
      color: 'text-purple-600 bg-purple-50 border-purple-200',
      data: implementationStrategy,
      type: 'string' as const
    },
    {
      key: 'developmentPhases',
      title: 'Fases de Desenvolvimento',
      icon: CalendarDaysIcon,
      color: 'text-indigo-600 bg-indigo-50 border-indigo-200',
      data: developmentPhases,
      type: 'array' as const
    },
    {
      key: 'teamRequirements',
      title: 'Requisitos da Equipe',
      icon: UserGroupIcon,
      color: 'text-orange-600 bg-orange-50 border-orange-200',
      data: teamRequirements,
      type: 'string' as const
    }
  ].filter(section => {
    if (!section.data) return false;
    if (section.type === 'array') return Array.isArray(section.data) && section.data.length > 0;
    if (section.type === 'object') return section.data && typeof section.data === 'object' && Object.keys(section.data).length > 0;
    return typeof section.data === 'string' && section.data.trim().length > 0;
  });

  if (sections.length === 0) return null;

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <div className="flex items-center space-x-2 mb-4">
        <CogIcon className="h-5 w-5 text-indigo-600" />
        <h3 className="text-md font-medium text-gray-900">
          Análise Técnica Detalhada
        </h3>
        <span className="text-xs bg-indigo-100 text-indigo-800 px-2 py-1 rounded-full">
          Gerado por IA
        </span>
      </div>

      <div className="space-y-3">
        {sections.map(({ key, title, icon: Icon, color, data, type }) => {
          const isExpanded = expandedSections[key];

          return (
            <div key={key} className={`rounded-lg border p-3 ${color}`}>
              <button
                onClick={() => toggleSection(key)}
                className="w-full flex items-center justify-between text-left"
              >
                <div className="flex items-center space-x-2">
                  <Icon className="h-4 w-4" />
                  <span className="text-sm font-medium">{title}</span>
                  {type === 'array' && (
                    <span className="text-xs bg-white bg-opacity-70 px-2 py-0.5 rounded-full">
                      {(data as any[]).length}
                    </span>
                  )}
                </div>
                {isExpanded ? (
                  <ChevronDownIcon className="h-4 w-4" />
                ) : (
                  <ChevronRightIcon className="h-4 w-4" />
                )}
              </button>

              {isExpanded && (
                <div className="mt-3 pt-3 border-t border-white border-opacity-50">
                  {type === 'array' && Array.isArray(data) && (
                    <ul className="space-y-2">
                      {data.map((item, index) => (
                        <li key={index} className="text-sm flex items-start space-x-2">
                          <span className="w-1.5 h-1.5 bg-current rounded-full mt-2 flex-shrink-0" />
                          <span>{String(item)}</span>
                        </li>
                      ))}
                    </ul>
                  )}

                  {type === 'object' && data && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {Object.entries(data as any).map(([req, value]) => (
                        <div key={req} className="bg-white bg-opacity-50 rounded p-2">
                          <div className="text-xs font-medium text-current mb-1 capitalize">
                            {req.replace(/([A-Z])/g, ' $1').toLowerCase()}
                          </div>
                          <div className="text-sm">{String(value)}</div>
                        </div>
                      ))}
                    </div>
                  )}

                  {type === 'string' && (
                    <p className="text-sm leading-relaxed whitespace-pre-wrap">
                      {data as string}
                    </p>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}